import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { RequestStatus } from 'legacy/lut/configs/service'

import { requestApplySobCompetitorChanges } from 'partner360/features/competitorsSob/api/requestApplySobCompetitorChanges'
import { requestApplySobCompetitorsComment } from 'partner360/features/competitorsSob/api/requestApplySobCompetitorsComment'
import { requestCompetitorsForSelect } from 'partner360/features/competitorsSob/api/requestCompetitorsForSelect'
import { requestSobCompetitorsComment } from 'partner360/features/competitorsSob/api/requestSobCompetitorsComment'
import { getRecalculatedOthersForAllFields } from 'partner360/features/competitorsSob/helpers/getRecalculatedOthersForAllFields'
import { getSelectedCompetitors } from 'partner360/features/competitorsSob/helpers/getSelectedCompetitors'
import { getUpdatedCompetitorData } from 'partner360/features/competitorsSob/helpers/getUpdatedCompetitorData'
import { mapPartialToFullCompetitor } from 'partner360/features/competitorsSob/helpers/mapPartialToFullCompetitor'
import { IActionChangeCompetitorSob } from 'partner360/features/competitorsSob/models/actionChangeCompetitorSob'
import { IActionChangeQBRSelectedCompetitors } from 'partner360/features/competitorsSob/models/actionChangeQBRSelectedCompetitors'
import { IActionChangeSobCompetitorsComment } from 'partner360/features/competitorsSob/models/actionChangeSobCompetitorsComment'
import { collapseTableRow } from 'partner360/helpers/financialTable/collapseTableRow'
import { expandTableRow } from 'partner360/helpers/financialTable/expandTableRow'
import { ISobCompetitor } from 'partner360/models/ISobCompetitor'

import { requestCompetitorsSob } from './api/requestCompetitorsSob'
import { initialState } from './configs'
import {
  HP_COMPETITOR_ID,
  OTHER_COMPETITOR_ID,
  PARTNER360_COMPETITORS_SOB_NAMESPACE,
} from './constants'
import { IActionExpandCollapseSobCompetitorsTable } from './models/actionExpandCollapseSobCompetitorsTable'

const competitorsSobSlice = createSlice({
  name: PARTNER360_COMPETITORS_SOB_NAMESPACE,
  initialState,
  reducers: {
    expandSobCompetitorsTableRow: (
      state,
      {
        payload: { keyField, keyFieldValue, programType },
      }: PayloadAction<IActionExpandCollapseSobCompetitorsTable>
    ) => {
      const competitors = state.data[programType]
      if (competitors && competitors.length) {
        state.data[programType] = expandTableRow<ISobCompetitor>({
          keyFieldValue,
          keyField,
          data: competitors,
        })
      }
    },
    resetSobCompetitorsData: (state, action: PayloadAction<ProgramTypes>) => {
      state.data[action.payload] = []
      state.initialData[action.payload] = []
      state.competitorsForSelect[action.payload] = []
      state.comment[action.payload] = ''
    },
    collapseSobCompetitorsTableRow: (
      state,
      {
        payload: { keyField, keyFieldValue, hierarchyField, programType },
      }: PayloadAction<IActionExpandCollapseSobCompetitorsTable>
    ) => {
      const competitors = state.data[programType]
      if (competitors && competitors.length) {
        state.data[programType] = collapseTableRow<ISobCompetitor>({
          keyField,
          keyFieldValue,
          hierarchyField,
          data: competitors,
        })
      }
    },
    changeSobCompetitor: (
      state,
      {
        payload: { entryId, competitorId, newValue, field, programType },
      }: PayloadAction<IActionChangeCompetitorSob>
    ) => {
      const competitors = state.data[programType]
      if (competitors && competitors.length) {
        state.data[programType] = getUpdatedCompetitorData({
          competitors: competitors,
          competitorId,
          field,
          entryId,
          value: newValue,
        })
      }
    },
    cancelSobCompetitorsChanges: (state, action: PayloadAction<ProgramTypes>) => {
      const programType = action.payload
      state.data[programType] = state.initialData[programType]
    },
    changeSelectedCompetitors: (
      state,
      {
        payload: { selectedCompetitors, programType },
      }: PayloadAction<IActionChangeQBRSelectedCompetitors>
    ) => {
      const competitors = state.data[programType]
      const competitorsForSelect = state.competitorsForSelect[programType] || []
      if (competitors && competitors.length) {
        const currentSelectedCompetitors = getSelectedCompetitors(competitors)
        if (currentSelectedCompetitors.length > selectedCompetitors.length) {
          const filtered = competitors.filter((el) => {
            if (el.CompetitorID === OTHER_COMPETITOR_ID || el.CompetitorID === HP_COMPETITOR_ID) {
              return true
            }
            return selectedCompetitors.includes(el.CompetitorID)
          })
          state.data[programType] = getRecalculatedOthersForAllFields(filtered)
        } else {
          const diff = selectedCompetitors.filter(
            (el) => !currentSelectedCompetitors.find((c) => c.CompetitorID === el)
          )

          const uniqueEntryObjects = competitors.reduce((acc, item) => {
            if (!acc.some((entry) => entry.EntryId === item.EntryId)) {
              acc.push({
                EntryId: item.EntryId,
                Name: item.Name,
                NameKey: item.NameKey,
                ParentId: item.ParentId,
              })
            }
            return acc
          }, [] as Partial<ISobCompetitor>[])

          const result = diff.reduce((acc, competitorId) => {
            const competitor = competitorsForSelect.find((el) => el.CompetitorID === competitorId)

            if (competitor) {
              const entries = uniqueEntryObjects.map((item) =>
                mapPartialToFullCompetitor({
                  CompetitorID: competitorId,
                  CompetitorName: competitor.Name,
                  ...item,
                })
              )

              return [...acc, ...entries] as ISobCompetitor[]
            }

            return acc
          }, [] as ISobCompetitor[])

          const otherCompetitor = competitors.filter(
            (el) => el.CompetitorID === OTHER_COMPETITOR_ID
          )

          const notOtherCompetitor = competitors.filter(
            (el) => el.CompetitorID !== OTHER_COMPETITOR_ID
          )

          const newWithSortOrder = result.map((el) => {
            const notOtherByGroup = notOtherCompetitor.filter((item) => item.EntryId === el.EntryId)
            const maxSortOrder = Math.max(...notOtherByGroup.map((el) => el.SortOrder || 0))
            return { ...el, SortOrder: maxSortOrder + notOtherByGroup.length + 1 }
          })

          const otherWithUpdatedSortOrder = otherCompetitor.map((el) => {
            const notOtherByGroup = [...notOtherCompetitor, ...newWithSortOrder].filter(
              (item) => item.EntryId === el.EntryId
            )
            const maxSortOrder = Math.max(...notOtherByGroup.map((el) => el.SortOrder || 0))
            return { ...el, SortOrder: maxSortOrder + notOtherByGroup.length + 1 }
          })

          state.data[programType] = getRecalculatedOthersForAllFields([
            ...notOtherCompetitor,
            ...newWithSortOrder,
            ...otherWithUpdatedSortOrder,
          ])
        }
      }
    },
    onChangeSobCompetitorsComment: (
      state,
      { payload: { programType, comment } }: PayloadAction<IActionChangeSobCompetitorsComment>
    ) => {
      state.comment[programType] = comment
    },
  },
  extraReducers(builder) {
    builder
      .addCase(requestCompetitorsSob.pending, (state) => {
        state.fetchCompetitorsStatus = RequestStatus.PENDING
      })
      .addCase(requestCompetitorsSob.rejected, (state) => {
        state.fetchCompetitorsStatus = RequestStatus.REJECTED
      })
      .addCase(requestCompetitorsSob.fulfilled, (state, action) => {
        if (action.payload) {
          const { programType, data, partnerId, fYear } = action.payload
          state.partnerId = partnerId
          state.fYear = fYear
          state.data = { ...state.data, [programType]: data }
          state.initialData = { ...state.data, [programType]: data }
          state.fetchCompetitorsStatus = RequestStatus.FULFILLED
        }
      })
      .addCase(requestApplySobCompetitorChanges.pending, (state) => {
        state.savingStatus = RequestStatus.PENDING
      })
      .addCase(requestApplySobCompetitorChanges.rejected, (state) => {
        state.savingStatus = RequestStatus.REJECTED
      })
      .addCase(requestApplySobCompetitorChanges.fulfilled, (state, action) => {
        if (action.payload) {
          const { data, programType } = action.payload
          state.data = { ...state.data, [programType]: data }
          state.initialData = { ...state.data, [programType]: data }
          state.savingStatus = RequestStatus.FULFILLED
        }
      })
      .addCase(requestCompetitorsForSelect.pending, (state) => {
        state.fetchCompetitorsForSelectStatus = RequestStatus.PENDING
      })
      .addCase(requestCompetitorsForSelect.rejected, (state) => {
        state.fetchCompetitorsForSelectStatus = RequestStatus.REJECTED
      })
      .addCase(requestCompetitorsForSelect.fulfilled, (state, action) => {
        if (action.payload) {
          const { data, programType } = action.payload
          state.competitorsForSelect = { ...state.competitorsForSelect, [programType]: data }
          state.fetchCompetitorsForSelectStatus = RequestStatus.FULFILLED
        }
      })

      .addCase(requestSobCompetitorsComment.pending, (state) => {
        state.commentStatus = RequestStatus.PENDING
      })
      .addCase(requestSobCompetitorsComment.rejected, (state) => {
        state.commentStatus = RequestStatus.REJECTED
      })
      .addCase(requestSobCompetitorsComment.fulfilled, (state, action) => {
        if (action.payload) {
          const { comment, programType } = action.payload
          state.comment = { ...state.comment, [programType]: comment }
          state.commentStatus = RequestStatus.FULFILLED
        }
      })

      .addCase(requestApplySobCompetitorsComment.pending, (state) => {
        state.commentStatus = RequestStatus.PENDING
      })
      .addCase(requestApplySobCompetitorsComment.rejected, (state) => {
        state.commentStatus = RequestStatus.REJECTED
      })
      .addCase(requestApplySobCompetitorsComment.fulfilled, (state) => {
        state.commentStatus = RequestStatus.FULFILLED
      })
  },
})

export const {
  expandSobCompetitorsTableRow,
  collapseSobCompetitorsTableRow,
  changeSobCompetitor,
  changeSelectedCompetitors,
  cancelSobCompetitorsChanges,
  resetSobCompetitorsData,
  onChangeSobCompetitorsComment,
} = competitorsSobSlice.actions

export default competitorsSobSlice.reducer
